import { useEffect, useMemo, useState } from "react";
import { env } from "@/lib/env";
import { fetchSimilarityIndex, type SimilarityIndex } from "@/lib/similarVenues";
import { listMyVisits, type MyVisit } from "@/lib/visits";
import { useShowcase } from "@/services/showcase";

/**
 * The signed-in diner's inputs to "Recommended for you": their visit history
 * and (beta) the embedding neighbours of the venues they've visited. Neither
 * refreshes mid-session, so the strip never reshuffles under the visitor.
 * Inside the `/showcase-cluj` demo the history is the demo persona's (no fetch).
 */
export function usePersonalization(signedIn: boolean) {
  const showcase = useShowcase();
  // Visit history for the "Recommended for you" strip — fetched once per
  // sign-in (null = still loading → skeletons). Like tapHistory, it is NOT
  // refreshed mid-session, so the strip never reshuffles under the visitor;
  // today's visits bias the next mount. listMyVisits is fail-soft ([] on any
  // error), and auth-initializing reads as signed-out, so guests never fetch.
  const [myVisits, setMyVisits] = useState<MyVisit[] | null>(null);
  useEffect(() => {
    if (showcase) {
      setMyVisits(showcase.myVisits);
      return;
    }
    if (!signedIn) {
      setMyVisits(null);
      return;
    }
    let cancelled = false;
    listMyVisits().then((visits) => {
      if (!cancelled) setMyVisits(visits);
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn, showcase]);
  // It-7 (beta, off by default): embedding neighbours of the venues this diner
  // has visited, so the strip can surface kinship the taxonomy slugs miss.
  // With the flag off nothing is fetched at all — not fetched-and-ignored — and
  // fetchSimilarityIndex is fail-soft, so a pre-§30 DB just leaves it null.
  // The strip stays in skeletons until this resolves, so it can't visibly
  // reshuffle a beat after painting (same promise as myVisits above).
  const [similarity, setSimilarity] = useState<SimilarityIndex | null>(null);
  const [similarityPending, setSimilarityPending] = useState(false);
  const visitedIdsKey = useMemo(
    () => (myVisits ? Array.from(new Set(myVisits.map((v) => v.venue_id))).sort().join(",") : ""),
    [myVisits],
  );
  useEffect(() => {
    if (!env.AI_SIMILARITY || showcase || !signedIn || !visitedIdsKey) {
      setSimilarity(null);
      setSimilarityPending(false);
      return;
    }
    let cancelled = false;
    setSimilarityPending(true);
    fetchSimilarityIndex(visitedIdsKey.split(",")).then((index) => {
      if (cancelled) return;
      setSimilarity(index);
      setSimilarityPending(false);
    });
    return () => {
      cancelled = true;
    };
  }, [signedIn, visitedIdsKey, showcase]);

  return { myVisits, similarity, similarityPending };
}
